import { Box, Menu, MenuItem, Stack, Typography } from '@mui/material';
import BriefCaseIcon from '@/app/assets/icons/briefcase.svg?react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useState } from 'react';
import { logout } from '@/entities/authSlice';
import { useLogoutUserMutation } from '@/entities/auth/api';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useGetFactorsBranchesQuery } from '@/app/api';
import { getFetchEror } from '@/app/helpers';
import { useNotification } from '@/shared/contexts/NotificationContext';

export const ProfileCompanyMenu = () => {
  const { t } = useTranslation();
  const { profile, cabinetType, refreshToken } = useAppSelector(
    (state) => state.auth
  );
  const [logoutUser] = useLogoutUserMutation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const shortName = profile?.name?.match(/"(.*?)"/)?.[1];
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const dispatch = useAppDispatch();

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    try {
      if (refreshToken) {
        await logoutUser({ refreshToken }).unwrap();
      }
    } catch (error) {
      showNotification(getFetchEror(error));
    }
    dispatch(logout());
  };

  const handleSettingsClick = () => {
    navigate('/factor/settings');
    handleClose();
  };
  const isFactor = cabinetType === 'factor';

  const { data: branches } = useGetFactorsBranchesQuery(undefined, {
    skip: !profile || !isFactor,
  });

  if (!profile) return null;

  const branchName = branches?.data?.find(
    (branch) => branch.unique === Number(profile?.branchId)
  )?.branchName;

  return (
    <>
      <Stack
        component='div'
        id='profile-company-menu'
        aria-controls={open ? 'profile-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}
        direction='row'
        spacing={1.5}
        alignItems='center'
        role='button'
        sx={{
          p: 1,
          borderRadius: '6px',
          transition: 'background-color 0.2s',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: '#fff',
          },
        }}
      >
        <Stack
          alignItems='center'
          justifyContent='center'
          sx={{
            width: 40,
            height: 40,
            bgcolor: 'rgba(21, 85, 252, 0.04)',
            borderRadius: '6px',
          }}
        >
          <Box component={BriefCaseIcon} />
        </Stack>

        <Box
          sx={{
            maxWidth: 250,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 13,
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              overflow: 'hidden',
            }}
          >
            {shortName}
            {profile.branchId ? `: ${branchName}` : ''}
          </Typography>

          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 500,
              color: 'rgba(11, 19, 112, .5)',
            }}
          >
            {profile?.tin}
          </Typography>
        </Box>
      </Stack>

      <Menu
        id='profile-menu'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {isFactor && (
          <MenuItem onClick={handleSettingsClick}>
            {t('settings.title')}
          </MenuItem>
        )}
        <MenuItem onClick={handleLogout}>{t('logout')}</MenuItem>
      </Menu>
    </>
  );
};
